import type { CircularProgressProps as MuiCircularProgressProps } from '@mui/material';
import type { Replace, ValueOf } from '@kc-console/core';

const CircleProgressSizeSet = ['xs', 'sm', 'md', 'lg', 'max'] as const;
type CircleProgressSize = ValueOf<typeof CircleProgressSizeSet>;

const CircleProgressColorSet = ['default', 'inverse'] as const;
type CircleProgressColor = ValueOf<typeof CircleProgressColorSet>;

const CircleProgressSizeMap: Record<CircleProgressSize, string | number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  max: '100%',
};

const CircleProgressThicknessMap: Record<CircleProgressSize, number> = {
  xs: 5,
  sm: 5,
  md: 5,
  lg: 5,
  max: 2,
};

type PropsOverrides = {
  /**
   * CircleProgress의 방향을 결정합니다.
   *
   * true일 때 시계 방향으로 돌고, false일 때 반시계 방향으로 돕니다.
   * @default true
   */
  clockwise?: boolean;
  /**
   * CircleProgress 컴포넌트의 색상을 설정합니다.
   * @default default
   */
  color?: CircleProgressColor;
  /**
   * Progress 값의 최대치입니다.
   * Progress value 계산식
   * const calcValue = ((value - min) / (max - min)) * 100;
   *  - calcValue가 100보다 클 경우, CircleProgress 컴포넌트의 value는 100으로 표현됩니다.
   * @default 100
   */
  max?: number;
  /**
   * Progress 값의 최소치입니다.
   * Progress value 계산식
   * const calcValue = ((value - min) / (max - min)) * 100;
   *  - calcValue가 0보다 작을 경우, CircularProgress 컴포넌트의 value는 0으로 표현됩니다.
   * @default 0
   */
  min?: number;
  /**
   * true 일 때, CircleProgress 가운데 영역에 퍼센티지 라벨을 표시합니다.
   * size가 "max" 일 때에만 유효합니다.
   * @default false
   */
  showPercentage?: boolean;
  /**
   * CircleProgress의 크기를 설정합니다.
   * xs: 12 x 12
   * sm: 16 x 16
   * md: 20 x 20
   * lg: 24 x 24
   * max: 100% x 100% (상위 element inherit)
   * @default max
   */
  size?: CircleProgressSize;
  /**
   * CircleProgress의 두께를 설정합니다.
   * 크기 별로 지정된 값이 별도로 존재하지만, props로 별도의 값을 전달할 경우 커스텀 thickness가 우선순위 높게 설정됩니다.
   * xs: 5
   * sm: 5
   * md: 5
   * lg: 5
   * max: 2
   */
  thickness?: number;
};

type CircleProgressProps = Replace<Omit<MuiCircularProgressProps, 'variant'>, PropsOverrides>;

type CircleProgressOwnerState = CircleProgressProps;

export type {
  CircleProgressProps,
  CircleProgressSize,
  CircleProgressOwnerState,
  CircleProgressColor,
};

export {
  CircleProgressSizeSet,
  CircleProgressSizeMap,
  CircleProgressThicknessMap,
  CircleProgressColorSet,
};
